import { useQuery } from '@tanstack/react-query';
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';

interface ReferralCommission {
  id: string;
  referred_user_id: string;
  gross_amount: number;
  net_commission: number;
  commission_percentage: number;
  status: 'pending' | 'approved' | 'paid';
  transaction_date: string;
}

interface ReferralPayout {
  id: string;
  total_commission: number;
  status: string;
  scheduled_date: string;
}

interface PractitionerEarningsResponse {
  practitioner_id: string;
  referral_code: string;
  code_status: string;
  total_signups: number;  // NEW: All users who signed up (paid or not)
  paying_referrals: number;  // NEW: Users who made payments
  total_referrals: number;  // Alias for backward compatibility
  total_revenue_generated: number;
  total_commission_earned: number;
  pending_commission: number;
  approved_commission: number;
  paid_commission: number;
  total_payouts: number;
  last_payout_date: string | null;
  recent_commissions: ReferralCommission[];
  recent_payouts: ReferralPayout[];
}

interface ReferralEarning {
  id: string;
  referred_user_id: string;
  session_id: string;
  payment_transaction_id: string;
  session_amount: number;
  revenue_share_percentage: number;
  earnings_amount: number;
  tier_at_time: string;
  payout_status: 'pending' | 'paid' | 'processing' | 'cancelled';
  payout_date: string | null;
  created_at: string;
}

interface ReferralStats {
  totalEarnings: number;
  monthlyEarnings: number;
  totalReferrals: number;
  activeReferrals: number;
  currentTier: {
    tier_name: string;
    revenue_share_percentage: number;
    min_active_referrals: number;
  };
  nextTier: {
    tier_name: string;
    revenue_share_percentage: number;
    min_active_referrals: number;
  } | null;
}

export const useReferralEarnings = () => {
  const { user } = useAuth();

  // Fetch practitioner earnings from the new commission system
  const {
    data: practitionerEarnings,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['practitioner-earnings', user?.id],
    queryFn: async () => {
      if (!user) return null;

      try {
        const apiBaseUrl = import.meta.env.VITE_BACKEND_URL || '/api/v1';
        const response = await fetch(`${apiBaseUrl}/referrals/practitioner/earnings`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${(await backendClient.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          // If 404, practitioner doesn't have a referral code yet
          if (response.status === 404) {
            return null;
          }
          throw new Error(`Failed to fetch earnings: ${response.statusText}`);
        }

        const data: PractitionerEarningsResponse = await response.json();
        return data;
      } catch (err) {
        console.error('Error fetching practitioner earnings:', err);
        throw err;
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  // Get active referrals (users who made purchases in last 30 days) - FIXED
  const {
    data: activeReferralsCount = 0,
  } = useQuery({
    queryKey: ['active-referrals', user?.id],
    queryFn: async () => {
      if (!user) return 0;

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // FIXED: Query referral_commissions instead of payment_transactions
      const { data: activeReferralsData } = await backendClient
        .from('referral_commissions')
        .select('referred_user_id')
        .eq('practitioner_id', user.id)
        .gte('transaction_date', thirtyDaysAgo.toISOString());

      // Count unique referred users
      return new Set(activeReferralsData?.map((c: { referred_user_id: string }) => c.referred_user_id) || []).size;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // Get current tier based on active referrals
  const {
    data: tierData,
  } = useQuery({
    queryKey: ['referral-tiers', activeReferralsCount],
    queryFn: async () => {
      // Get current tier
      const { data: currentTier } = await backendClient
        .from('referral_settings')
        .select('*')
        .lte('min_active_referrals', activeReferralsCount)
        .eq('is_active', true)
        .order('min_active_referrals', { ascending: false })
        .limit(1)
        .maybeSingle();

      // Get next tier
      const { data: nextTier } = await backendClient
        .from('referral_settings')
        .select('*')
        .gt('min_active_referrals', activeReferralsCount)
        .eq('is_active', true)
        .order('min_active_referrals', { ascending: true })
        .limit(1)
        .maybeSingle();

      return {
        currentTier: currentTier || {
          tier_name: 'Standard',
          revenue_share_percentage: 20,
          min_active_referrals: 0
        },
        nextTier
      };
    },
    enabled: typeof activeReferralsCount === 'number',
    staleTime: 5 * 60 * 1000,
  });

  // Transform commission data to match legacy ReferralEarning format for backwards compatibility
  const earnings: ReferralEarning[] = practitionerEarnings?.recent_commissions?.map(commission => ({
    id: commission.id,
    referred_user_id: commission.referred_user_id,
    session_id: '', // Not available in new system
    payment_transaction_id: '', // Not available in new system
    session_amount: commission.gross_amount,
    revenue_share_percentage: commission.commission_percentage,
    earnings_amount: commission.net_commission,
    tier_at_time: 'Standard', // Not tracked in new system
    payout_status: commission.status === 'paid' ? 'paid' : commission.status === 'approved' ? 'processing' : 'pending',
    payout_date: null, // Would need to join with payouts
    created_at: commission.transaction_date,
  })) || [];

  // Calculate monthly earnings from commissions
  const monthlyEarnings = (() => {
    if (!practitionerEarnings?.recent_commissions) return 0;

    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    return practitionerEarnings.recent_commissions
      .filter(c => new Date(c.transaction_date) >= startOfMonth)
      .reduce((sum, c) => sum + c.net_commission, 0);
  })();

  const stats: ReferralStats | null = practitionerEarnings && tierData ? {
    totalEarnings: practitionerEarnings.paid_commission, // Only count paid commissions as total earnings
    monthlyEarnings,
    totalReferrals: practitionerEarnings.total_signups,  // FIXED: Show ALL signups, not just paying
    activeReferrals: activeReferralsCount,
    currentTier: tierData.currentTier,
    nextTier: tierData.nextTier
  } : null;

  return {
    earnings,
    stats,
    practitionerEarnings,  // NEW: Expose raw earnings data for paying_referrals access
    isLoading,
    error,
    refetch
  };
};
